import React, { useCallback, useEffect, useState } from 'react';
import type { DeadlineDto, DeadlineRepeat } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { euro, euroInput, parseEuro } from '../format';
import { Empty, ErrorBanner, Field, Modal, More, Panel } from '../ui';
import { DateInput } from '../pickers';
import { ask } from '../dialogs';

const REPEAT: Record<DeadlineRepeat, string> = { none: 'Una volta', monthly: 'Ogni mese', quarterly: 'Ogni trimestre', yearly: 'Ogni anno' };
const CATEGORIES = ['F24', 'IVA', 'Bollo', 'Imposte', 'INPS', 'Camera di Commercio', 'Dichiarazioni', 'Altro'];

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dateIt = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
const daysTo = (d: string) => Math.round((new Date(d + 'T12:00:00').getTime() - new Date(todayKey() + 'T12:00:00').getTime()) / 86_400_000);

/** Scadenzario fiscale: F24, IVA, bolli e altri appuntamenti con il fisco, con promemoria. */
export function DeadlinesPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [list, setList] = useState<DeadlineDto[] | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [editing, setEditing] = useState<DeadlineDto | 'new' | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await api.get<DeadlineDto[]>('/api/deadlines'));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (d: DeadlineDto) => {
    try {
      await api.post(`/api/deadlines/${d.id}/done`, { done: !d.doneAt });
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const seed = async () => {
    try {
      const r = await api.post<{ added: number }>('/api/deadlines/seed-standard');
      setInfo(r.added ? `Aggiunte ${r.added} scadenze. Le date sono indicative: verificale con il tuo commercialista.` : 'Le scadenze standard sono già presenti.');
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const pending = (list ?? []).filter((d) => !d.doneAt);
  const done = (list ?? []).filter((d) => d.doneAt);
  const soon = pending.filter((d) => daysTo(d.dueDate) <= 30).length;

  return (
    <div className="panels single">
      <Panel
        title="Scadenzario fiscale"
        subtitle={list ? `Tasse e pagamenti da non dimenticare: ${pending.length} da fare${soon ? `, ${soon} entro 30 giorni` : ''}` : undefined}
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            <button type="button" className="btn" onClick={() => void seed()} title="Aggiunge le scadenze di ogni anno: F24, bolli, acconti, Camera di Commercio">
              Scadenze standard
            </button>
            <button type="button" className="btn dark" title="Aggiungi una scadenza" onClick={() => setEditing('new')}>
              <Icon name="plus" size={16} /> Nuova
            </button>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {info && (
          <div className="note good" style={{ marginBottom: 12 }} onClick={() => setInfo(null)}>
            {info}
          </div>
        )}
        {!list ? (
          <div className="muted small">Caricamento…</div>
        ) : pending.length === 0 ? (
          <Empty icon="calendar">Nessuna scadenza da fare. Usa «Scadenze standard» per partire.</Empty>
        ) : (
          <div className="ag-list">
            {pending.map((d) => {
              const n = daysTo(d.dueDate);
              return (
                <div key={d.id} className="ag-row" onClick={() => setEditing(d)}>
                  <button
                    type="button"
                    className="ag-check"
                    title="Segna come fatta"
                    onClick={(e) => {
                      e.stopPropagation();
                      void toggle(d);
                    }}
                  >
                    <Icon name="circle" size={16} />
                  </button>
                  <span className="ag-title">
                    <b>{d.title}</b>
                    <div className="muted small">
                      {d.category} · {REPEAT[d.repeatEvery]}
                      {d.amountCents ? ` · ${euro(d.amountCents)}` : ''}
                    </div>
                  </span>
                  <span className={n < 0 ? 'kan-due late' : n <= d.remindDays ? 'kan-due soon' : 'muted small'}>
                    {dateIt(d.dueDate)}
                    {n < 0 ? ` · scaduta da ${-n} g` : n === 0 ? ' · oggi' : n <= 30 ? ` · tra ${n} g` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {done.length > 0 && (
          <>
            <button type="button" className="btn ghost small" style={{ marginTop: 14 }} onClick={() => setShowDone((v) => !v)}>
              {showDone ? 'Nascondi' : 'Mostra'} completate ({done.length})
            </button>
            {showDone && (
              <div className="ag-list" style={{ marginTop: 8 }}>
                {done.map((d) => (
                  <div key={d.id} className="ag-row done" onClick={() => setEditing(d)}>
                    <button
                      type="button"
                      className="ag-check"
                      title="Segna come da fare"
                      onClick={(e) => {
                        e.stopPropagation();
                        void toggle(d);
                      }}
                    >
                      <Icon name="check" size={16} />
                    </button>
                    <span className="ag-title">{d.title}</span>
                    <span className="muted small">{dateIt(d.dueDate)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Panel>
      {editing && (
        <DeadlineModal
          ctx={ctx}
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function DeadlineModal({ ctx, item, onClose, onSaved }: { ctx: Ctx; item: DeadlineDto | null; onClose: () => void; onSaved: () => void }) {
  const { api } = ctx;
  const [title, setTitle] = useState(item?.title ?? '');
  const [category, setCategory] = useState(item?.category ?? 'F24');
  const [due, setDue] = useState(item?.dueDate ?? todayKey());
  const [repeat, setRepeat] = useState<DeadlineRepeat>(item?.repeatEvery ?? 'none');
  const [remind, setRemind] = useState(String(item?.remindDays ?? 7));
  const [amount, setAmount] = useState(item?.amountCents ? euroInput(item.amountCents) : '');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!title.trim()) return setError('Indica il titolo');
    setBusy(true);
    const body = { title: title.trim(), category, dueDate: due, repeatEvery: repeat, remindDays: Math.max(0, Number(remind) || 0), amountCents: parseEuro(amount) || null, notes: notes.trim() || null };
    try {
      if (item) await api.put(`/api/deadlines/${item.id}`, body);
      else await api.post('/api/deadlines', body);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item || !(await ask(`Eliminare "${item.title}"?`))) return;
    try {
      await api.del(`/api/deadlines/${item.id}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Modal
      title={item ? 'Scadenza' : 'Nuova scadenza'}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          {item && (
            <button type="button" className="btn ghost" onClick={() => void remove()} title="Elimina la scadenza">
              <Icon name="trash" size={15} />
            </button>
          )}
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <Field label="Titolo">
        <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus={!item} placeholder="Es. F24 IVA primo trimestre" />
      </Field>
      <div className="fields-2">
        <Field label="Scade il">
          <DateInput value={due} onChange={setDue} />
        </Field>
        <Field label="Categoria">
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {[...new Set([category, ...CATEGORIES])].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <More open={!!(item && (item.repeatEvery !== 'none' || item.remindDays !== 7 || item.amountCents || item.notes))}>
        <div className="fields-2">
          <Field label="Si ripete" hint="Quando la segni fatta, la successiva si crea da sola">
            <select value={repeat} onChange={(e) => setRepeat(e.target.value as DeadlineRepeat)}>
              {(Object.keys(REPEAT) as DeadlineRepeat[]).map((r) => (
                <option key={r} value={r}>
                  {REPEAT[r]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Avvisami (giorni prima)">
            <input type="number" min={0} max={90} value={remind} onChange={(e) => setRemind(e.target.value)} />
          </Field>
          <Field label="Importo previsto (€)">
            <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="Facoltativo" />
          </Field>
          <Field label="Note">
            <input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
      </More>
    </Modal>
  );
}
